import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useDynamicLink } from '@/hooks/app/useDynamicLink';
interface DynamicLinkProps {
  href: string;
  children: React.ReactNode;
}
/**
 * DynamicLink Component
 * 处理 example.com 链接，提供悬浮提示和 PDF 跳转功能
 */
export default function DynamicLink({ href, children }: DynamicLinkProps) {
  const { t } = useTranslation();
  const {
    linkData,
    isLoading,
    showTooltip,
    tooltipPosition,
    truncateText,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltipMouseEnter,
    handleClick,
  } = useDynamicLink(href);
  return (
    <span className="relative inline-block">
      <a
        href="#"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          color: '#0066cc',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {children}
      </a>
      {/* Hover tooltip */}
      {showTooltip && linkData?.showContent && (
        <div
          className="fixed z-50 p-3 bg-white text-gray-700 text-sm rounded-lg shadow-lg border border-gray-200 max-w-md"
          style={{
            ...(tooltipPosition.top !== null && {
              top: `${tooltipPosition.top}px`,
            }),
            ...(tooltipPosition.bottom !== null && {
              bottom: `${tooltipPosition.bottom}px`,
            }),
            left: `${tooltipPosition.left}px`,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <div className="mb-1 last:mb-0 text-sm">{children}</div>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                code: ({ children }) => (
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                    {children}
                  </code>
                ),
                a: ({ href, children }) => (
                  <a href={href} className="text-blue-600 underline">
                    {children}
                  </a>
                ),
              }}
            >
              {truncateText(linkData.showContent)}
            </ReactMarkdown>
          </div>
          {/* Arrow indicator */}
          <div
            className={`absolute w-0 h-0 border-l-8 border-r-8 border-l-transparent border-r-transparent ${
              tooltipPosition.placement === 'top'
                ? 'top-full border-t-8 border-t-white'
                : 'bottom-full border-b-8 border-b-white'
            }`}
            style={{
              left: tooltipPosition.linkRect
                ? Math.max(
                    8,
                    Math.min(
                      (tooltipPosition.linkRect as DOMRect).left +
                        (tooltipPosition.linkRect as DOMRect).width / 2 -
                        tooltipPosition.left -
                        8,
                      350 - 24
                    )
                  )
                : 16,
            }}
          ></div>
        </div>
      )}
      {/* Loading indicator */}
      {showTooltip && isLoading && (
        <div className="absolute z-50 p-2 bg-white text-gray-700 text-xs rounded shadow-lg bottom-full left-0 mb-2">
          {t('common.loading')}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
        </div>
      )}
    </span>
  );
}
